<template>
	<div class="crRoot">
		<h1 class="pageHeading">Code Review</h1>
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }" class="active" aria-current="page">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }">Report</router-link>
		</nav>

		<div v-if="summaryError" class="errorBox">{{summaryError}}</div>
		<div v-if="summary" class="card summary">
			<div class="row">
				<span class="summaryLabel">Open:</span>
				<span v-for="s in summary.openBySeverity" :key="s.name" class="badge" :class="severityClass(s.name)">{{s.count}} {{s.name}}</span>
				<button type="button" v-if="summary.openSecurityCount" class="badge securityBadge badgeButton" @click="showOpenSecurity" title="Show the open security findings">{{summary.openSecurityCount}} security</button>
				<span v-if="summary.fixPushedCount" class="badge stProgress">{{summary.fixPushedCount}} fix pushed</span>
				<span v-if="summary.openByRepo.length" class="hintText">
					by repository:
					<template v-for="(r, i) in summary.openByRepo" :key="r.name">{{i ? ', ' : ''}}{{r.name}} {{r.count}}</template>
				</span>
			</div>
			<div class="row">
				<span class="summaryLabel">Pushes:</span>
				<template v-for="t in visibleTargetCounts" :key="t.name">
					<router-link :to="{ name: 'codeReviewPushes', query: { status: t.name } }" class="badge" :class="statusClass(t.name)">{{t.count}} {{label(t.name)}}</router-link>
				</template>
				<span class="hintText">(in progress: all; others: pushed in the last 30 days)</span>
			</div>
			<div class="row hintText">
				<span v-if="!summary.enabled" class="badge stNeutral">Reviews are turned off (codeReviewEnabled)</span>
				<span>Poller: {{summary.pollerStatus}}</span>
			</div>
		</div>

		<div class="card">
			<form class="filters" @submit.prevent="load">
				<div class="filterItem">
					<label for="crqSeverity">Minimum severity</label>
					<select id="crqSeverity" v-model="filter.minSeverity" @change="filterChanged">
						<option value="">Any</option>
						<option v-for="s in severities" :key="s" :value="s">{{s}}</option>
					</select>
				</div>
				<div class="filterItem">
					<label for="crqText">Title, file or CR-id</label>
					<input id="crqText" type="search" v-model="filter.text" @input="textChanged" placeholder="contains…" />
				</div>
				<fieldset class="statusSet">
					<legend>Status</legend>
					<label v-for="s in findingStatuses" :key="s" class="statusOption">
						<input type="checkbox" :value="s" v-model="filter.statuses" @change="filterChanged" />
						{{label(s)}}
					</label>
				</fieldset>
				<fieldset class="statusSet">
					<legend>Repository <span class="legendHint">(none ticked: all)</span></legend>
					<label v-for="r in repos" :key="r" class="statusOption">
						<input type="checkbox" :value="r" v-model="filter.repos" @change="filterChanged" />
						{{r}}
					</label>
				</fieldset>
				<fieldset class="statusSet">
					<legend>Verdict <span class="legendHint">(none ticked: all)</span></legend>
					<label v-for="v in verdicts" :key="v" class="statusOption" :title="verdictTitles[v]">
						<input type="checkbox" :value="v" v-model="filter.verdicts" @change="filterChanged" />
						{{verdictLabel(v)}}
					</label>
				</fieldset>
				<fieldset class="statusSet">
					<legend>Category <span class="legendHint">(none ticked: all)</span></legend>
					<label v-for="c in categories" :key="c" class="statusOption">
						<input type="checkbox" :value="c" v-model="filter.categories" @change="filterChanged" />
						<span v-if="c === 'Security'" class="badge securityBadge">Security</span><template v-else>{{label(c)}}</template>
					</label>
				</fieldset>
				<fieldset v-if="pushers.length" class="statusSet">
					<legend>Pushed by <span class="legendHint">(none ticked: all)</span></legend>
					<label v-for="p in pushers" :key="p" class="statusOption">
						<input type="checkbox" :value="p" v-model="filter.pushers" @change="filterChanged" />
						{{p}}
					</label>
				</fieldset>
				<div class="filterItem buttons">
					<button type="submit" :disabled="loading">Refresh</button>
					<button type="button" @click="resetFilters" :disabled="loading">Reset filters</button>
				</div>
			</form>
		</div>

		<div v-if="error" class="errorBox">{{error}}</div>
		<div class="card tableCard">
			<div class="row">
				<span v-if="loading" class="loadingInline"><ScaleLoader /> Loading…</span>
				<span v-else class="hintText" role="status">{{findings.length}} finding{{findings.length === 1 ? '' : 's'}}{{truncated ? ' (only the newest ' + limit + ' are shown)' : ''}}{{filter.statuses.length ? '' : ' (no status chosen: showing Open and Fix pushed)'}}{{refreshing ? ' (refreshing…)' : ''}}</span>
				<span v-if="findings.length" class="hintText">Click a column heading to sort; Shift+click adds it as a further sort column.<template v-if="canWrite"> Tick rows to act on several at once (Shift+click ticks a range).</template></span>
			</div>
			<div v-if="canWrite && (selectedIds.length || bulkBusy || bulkMessage || bulkErrors.length)" class="bulkBar" role="region" aria-label="Bulk actions">
				<div class="row">
					<b>{{selectedIds.length}} selected</b>
					<button type="button" class="crAction danger" @click="openBulkReject" :disabled="bulkBusy || !selectedIds.length" title="Reject every selected finding with the same reason; an optional comment is added to each.">Reject…</button>
					<slide-button :key="'slide_fixed_' + slideCounter"
								  class="crSlide"
								  :auto-width="false"
								  :circle="true"
								  :noanimate="true"
								  :width="210"
								  :height="26"
								  :color="slideColors.positive"
								  text="slide to mark fixed"
								  success-text="marking"
								  :disabled="bulkBusy || !selectedIds.length"
								  title="Close every selected finding because the code no longer has the problem."
								  @completed="bulkMarkFixed" />
					<slide-button :key="'slide_reopen_' + slideCounter"
								  class="crSlide"
								  :auto-width="false"
								  :circle="true"
								  :noanimate="true"
								  :width="180"
								  :height="26"
								  :color="slideColors.plain"
								  text="slide to reopen"
								  success-text="reopening"
								  :disabled="bulkBusy || !selectedIds.length"
								  title="Set every selected finding back to Open."
								  @completed="bulkReopen" />
					<slide-button :key="'slide_reEvaluate_' + slideCounter"
								  class="crSlide"
								  :auto-width="false"
								  :circle="true"
								  :noanimate="true"
								  :width="200"
								  :height="26"
								  :color="slideColors.assist"
								  text="slide to re-evaluate"
								  success-text="queuing"
								  :disabled="bulkBusy || !selectedIds.length"
								  title="Queue an AI re-evaluation of every selected finding at the default branch's head (one Claude job each)."
								  @completed="bulkReEvaluate" />
					<button type="button" class="linkButton" @click="selectedIds = []" :disabled="bulkBusy || !selectedIds.length">Clear selection</button>
					<span v-if="bulkBusy" class="loadingInline" role="status"><ScaleLoader /> {{bulkProgress}}</span>
				</div>
				<div v-if="bulkMessage" class="hintText" role="status">{{bulkMessage}}</div>
				<div v-if="bulkErrors.length" class="errorBox" role="alert">
					<div v-for="(e, i) in bulkErrors" :key="i">{{e}}</div>
				</div>
			</div>
			<table v-if="findings.length" class="dense">
				<thead>
					<tr>
						<th v-if="canWrite" scope="col" class="selectCell">
							<input type="checkbox" :checked="allShownSelected" :indeterminate.prop="someShownSelected && !allShownSelected" @change="toggleAll($event.target.checked)" :disabled="bulkBusy" aria-label="Select all shown findings" title="Select all shown findings" />
						</th>
						<th v-for="c in columns" :key="c.key" scope="col" :aria-sort="ariaSort(c.key)">
							<button type="button" class="sortButton" @click="sortBy(c.key, $event)" :title="'Sort by ' + c.label.toLowerCase() + ' (Shift+click: add as a further sort column)'">
								{{c.label}}<span class="sortMark" aria-hidden="true">{{sortMark(c.key)}}</span>
							</button>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="f in sortedFindings" :key="f.id" class="clickable" :class="{ selectedRow: isSelected(f), securityRow: f.category === 'Security' }" @click="open(f, $event)">
						<td v-if="canWrite" class="selectCell" @click.stop="toggleRow(f, $event)">
							<input type="checkbox" :checked="isSelected(f)" :disabled="bulkBusy" :aria-label="'Select ' + f.displayId" @click.stop="setRow(f, $event.target.checked, $event.shiftKey)" />
						</td>
						<td class="nowrap"><router-link :to="findingRoute(f)">{{f.displayId}}</router-link></td>
						<td><span class="badge" :class="severityClass(f.effectiveSeverity)" :title="'Claimed ' + f.claimedSeverity">{{f.effectiveSeverity}}</span></td>
						<td class="nowrap">{{f.repo}}</td>
						<td class="fileCell">{{location(f)}}</td>
						<td class="titleCell"><span v-if="f.category === 'Security'" class="badge securityBadge rowBadge" title="Security finding">Security</span><span v-if="f.pendingSuggestion" class="badge suggestionBadge" title="An AI re-evaluation suggestion awaits a decision">AI suggestion</span> {{f.title}}<span v-if="f.duplicateOfId" class="hintText"> (duplicate of CR-{{f.duplicateOfId}})</span></td>
						<td><span class="badge" :class="statusClass(f.status)">{{label(f.status)}}</span></td>
						<td class="nowrap">{{verdictLabel(f.verdict)}}</td>
						<td class="nowrap">{{f.introducedByPush}}</td>
						<td class="nowrap">{{f.pusher || '—'}}</td>
						<td class="nowrap">{{formatDate(f.createdAt)}}</td>
					</tr>
				</tbody>
			</table>
			<div v-else-if="!loading && !error" class="hintText">No findings match the filters.</div>
		</div>

		<!-- Bulk reject dialog -->
		<div v-if="bulkRejectOpen" class="overlay" @keydown.esc="closeBulkReject">
			<div class="dialog" role="dialog" aria-modal="true" aria-labelledby="crqRejectTitle">
				<h2 id="crqRejectTitle" class="secondaryHeading">Reject {{selectedIds.length}} finding{{selectedIds.length === 1 ? '' : 's'}}</h2>
				<div class="hintText">{{selectedDisplayIds}}</div>
				<div id="crqRejectReasonLabel" class="dialogLabel">Reason</div>
				<div class="reasonButtons" role="group" aria-labelledby="crqRejectReasonLabel">
					<button v-for="(r, i) in rejectReasons" :key="r.value" type="button" :ref="i === 0 ? 'firstReason' : null" class="reasonButton" :class="{ selected: bulkRejectReason === r.value }" :aria-pressed="bulkRejectReason === r.value ? 'true' : 'false'" @click="bulkRejectReason = r.value">{{r.label}}</button>
				</div>
				<label for="crqRejectNote" class="dialogLabel">Comment (optional)</label>
				<textarea id="crqRejectNote" v-model="bulkRejectNote" rows="4" class="wide resizable" maxlength="2000" aria-describedby="crqRejectNoteHint"></textarea>
				<div id="crqRejectNoteHint" class="hintText">Added to the comments of each finding.</div>
				<div class="row dialogButtons">
					<button type="button" class="crAction danger" @click="confirmBulkReject" :disabled="bulkBusy || !bulkRejectReason">Reject {{selectedIds.length}}</button>
					<button type="button" @click="closeBulkReject" :disabled="bulkBusy">Cancel</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { GetSummary, ListFindings, SetStatus, ReEvaluate } from 'appRoot/api/CodeReviewData';
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { severities, findingStatuses, defaultQueueStatuses, categories, rejectReasons, slideColors, label, severityClass, statusClass, formatDate, location, loadStored, saveStored } from './codeReviewFormat';

	const storageKey = 'codeReview_queueFilters';

	// TriageVerdict names, most to least credible; "None" means not triaged.
	const verdicts = ['Confirmed', 'Plausible', 'Unverifiable', 'Intended', 'Refuted', 'None'];
	const verdictTitles = {
		Confirmed: 'Triage checked the code and the finding holds.',
		Plausible: 'Triage found it likely but could not fully confirm it.',
		Unverifiable: 'Triage could not check it (for example, it depends on code or data it cannot see).',
		Intended: 'Correct, but the change is deliberate and recorded as such (commit message, comment or document). Such findings are rejected automatically unless they are about security or claimed high or critical; tick Rejected under Status to see them.',
		Refuted: 'Triage checked the code and found the finding wrong.',
		None: 'Not triaged (triage failed or has not run).'
	};

	const columns = [
		{ key: 'id', label: 'ID', firstDir: 'desc' },
		{ key: 'severity', label: 'Severity', firstDir: 'desc' },
		{ key: 'repo', label: 'Repo', firstDir: 'asc' },
		{ key: 'file', label: 'File', firstDir: 'asc' },
		{ key: 'title', label: 'Title', firstDir: 'asc' },
		{ key: 'status', label: 'Status', firstDir: 'asc' },
		{ key: 'verdict', label: 'Verdict', firstDir: 'asc' },
		{ key: 'introduced', label: 'Introduced', firstDir: 'asc' },
		{ key: 'pusher', label: 'Pushed by', firstDir: 'asc' },
		{ key: 'created', label: 'Created', firstDir: 'desc' }
	];

	function defaultSort()
	{
		return [{ key: 'created', dir: 'desc' }];
	}

	function defaultFilter()
	{
		return { repos: [], minSeverity: '', text: '', statuses: defaultQueueStatuses.slice(), verdicts: [], categories: [], pushers: [], sort: defaultSort() };
	}

	/** Non-empty strings of a stored array or a route query value (a string or an array of them). */
	function stringList(value)
	{
		let list = Array.isArray(value) ? value : [value];
		return list.filter(s => typeof s === 'string' && s);
	}

	/**
	 * Query parameters another page links here with (e.g. the Fixes page: ?repo=DevGit&status=ApprovedBatch).  Each may repeat.
	 * They replace the filters (the sort order stays) and are then removed from the address.
	 */
	const routeFilterKeys = ['repo', 'status', 'minSeverity', 'verdict', 'category', 'pusher', 'text'];

	function rankIn(list, value)
	{
		let i = list.indexOf(value);
		return i === -1 ? list.length : i;
	}

	/** The value a finding is sorted by in a column: numbers compare numerically, strings case-insensitively. */
	function sortValue(f, key)
	{
		switch (key)
		{
			case 'id': return f.id;
			case 'severity': return -rankIn(severities, f.effectiveSeverity);
			case 'repo': return f.repo || '';
			case 'file': return location(f) || '';
			case 'title': return f.title || '';
			case 'status': return rankIn(findingStatuses, f.status);
			case 'verdict': return rankIn(verdicts, f.verdict);
			case 'introduced': return f.introducedByPush || '';
			case 'pusher': return f.pusher || '';
			case 'created': return f.createdAt || 0;
		}
		return 0;
	}

	function compareValues(a, b)
	{
		if (typeof a === 'number' && typeof b === 'number')
			return a - b;
		return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
	}

	export default {
		// Kept alive by ClientLayout's <keep-alive include="CodeReviewQueue"> while a finding is open.
		name: 'CodeReviewQueue',
		components: { SlideButton },
		data()
		{
			return {
				severities,
				findingStatuses,
				verdicts,
				verdictTitles,
				categories,
				columns,
				rejectReasons,
				slideColors,
				slideCounter: 0,
				summary: null,
				summaryError: null,
				filter: defaultFilter(),
				findings: [],
				truncated: false,
				limit: 0,
				loading: false,
				error: null,
				loadToken: 0,
				textTimer: null,
				selectedIds: [],
				selectionAnchorId: null,
				bulkBusy: false,
				bulkProgress: '',
				bulkMessage: '',
				bulkErrors: [],
				bulkRejectOpen: false,
				bulkRejectReason: null,
				bulkRejectNote: '',
				savedScroll: null,
				refreshing: false,
				activatedBefore: false
			};
		},
		beforeRouteLeave(to, from, next)
		{
			// Remember where the list was scrolled (the page or the layout around it scrolls, depending on the window size).
			this.savedScroll = this.scrollElements().map(el => el.scrollTop);
			next();
		},
		activated()
		{
			if (!this.activatedBefore)
			{
				this.activatedBefore = true; // The first activation is the initial mount; created() loads.
				return;
			}
			if (this.applyRouteFilters())
			{
				// Linked here with filters (this page stays alive in the background, so created() does not run again).
				this.savedScroll = null;
				this.$nextTick(() => this.scrollElements().forEach(el => { el.scrollTop = 0; }));
				this.loadSummary();
				this.load();
				return;
			}
			let saved = this.savedScroll;
			this.$nextTick(() =>
			{
				if (saved)
					this.scrollElements().forEach((el, i) => { el.scrollTop = saved[i] || 0; });
			});
			// Back from a finding: refresh quietly (the rows stay until the new list arrives), e.g. to show a status changed there.
			// Quietly also means without the taller "Loading…" spinner: a layout change above the rows right after the scroll position
			// is restored would make the browser's scroll anchoring move the list (28 px per round trip).
			this.loadSummary();
			this.load(true);
		},
		deactivated()
		{
			// The page shown next starts at the top.
			this.scrollElements().forEach(el => { el.scrollTop = 0; });
		},
		created()
		{
			let stored = loadStored(storageKey);
			if (stored && typeof stored === 'object')
			{
				let f = defaultFilter();
				if (Array.isArray(stored.repos))
					f.repos = stored.repos.filter(r => typeof r === 'string' && r);
				else if (typeof stored.repo === 'string' && stored.repo)
					f.repos = [stored.repo]; // Saved before the repository checkboxes.
				if (typeof stored.minSeverity === 'string' && (stored.minSeverity === '' || severities.indexOf(stored.minSeverity) !== -1))
					f.minSeverity = stored.minSeverity;
				if (typeof stored.text === 'string')
					f.text = stored.text;
				if (Array.isArray(stored.statuses))
					f.statuses = stored.statuses.filter(s => findingStatuses.indexOf(s) !== -1);
				if (Array.isArray(stored.verdicts))
					f.verdicts = stored.verdicts.filter(v => verdicts.indexOf(v) !== -1);
				if (Array.isArray(stored.categories))
					f.categories = stored.categories.filter(c => categories.indexOf(c) !== -1);
				if (Array.isArray(stored.pushers))
					f.pushers = stringList(stored.pushers);
				if (Array.isArray(stored.sort))
				{
					let sort = stored.sort.filter(s => s && columns.some(c => c.key === s.key) && (s.dir === 'asc' || s.dir === 'desc'));
					if (sort.length)
						f.sort = sort.map(s => ({ key: s.key, dir: s.dir }));
				}
				this.filter = f;
			}
			this.applyRouteFilters();
			this.loadSummary();
			this.load();
		},
		beforeUnmount()
		{
			clearTimeout(this.textTimer);
		},
		computed:
		{
			repos()
			{
				let list = this.summary ? this.summary.repos.slice() : [];
				for (let r of this.filter.repos)
					if (list.indexOf(r) === -1)
						list.push(r);
				return list;
			},
			pushers()
			{
				let list = this.summary && this.summary.pushers ? this.summary.pushers.slice() : [];
				for (let p of this.filter.pushers)
					if (list.indexOf(p) === -1)
						list.push(p);
				return list;
			},
			canWrite()
			{
				return !!(this.summary && this.summary.canWrite);
			},
			allShownSelected()
			{
				return this.findings.length > 0 && this.findings.every(f => this.selectedIds.indexOf(f.id) !== -1);
			},
			someShownSelected()
			{
				return this.findings.some(f => this.selectedIds.indexOf(f.id) !== -1);
			},
			selectedDisplayIds()
			{
				return this.selectedIds.map(id => 'CR-' + id).join(', ');
			},
			sortedFindings()
			{
				let sort = this.filter.sort;
				return this.findings.slice().sort((a, b) =>
				{
					for (let s of sort)
					{
						let c = compareValues(sortValue(a, s.key), sortValue(b, s.key));
						if (c !== 0)
							return s.dir === 'desc' ? -c : c;
					}
					return b.id - a.id;
				});
			},
			visibleTargetCounts()
			{
				if (!this.summary)
					return [];
				return this.summary.targetsByStatus.filter(t => t.count > 0 || t.name === 'Failed' || t.name === 'Safety');
			}
		},
		methods:
		{
			label,
			severityClass,
			statusClass,
			formatDate,
			location,
			/** The elements that may scroll the list: the page itself and the layout body around it. */
			scrollElements()
			{
				let list = [];
				if (this.$el && this.$el.nodeType === 1)
				{
					list.push(this.$el);
					if (this.$el.parentElement)
						list.push(this.$el.parentElement);
				}
				return list;
			},
			isSelected(f)
			{
				return this.selectedIds.indexOf(f.id) !== -1;
			},
			/** Ticks or unticks a row; with Shift, every row between the last one clicked and this one (in the shown order) too. */
			setRow(f, checked, range)
			{
				let ids = [f.id];
				if (range && this.selectionAnchorId !== null)
				{
					let order = this.sortedFindings.map(x => x.id);
					let a = order.indexOf(this.selectionAnchorId);
					let b = order.indexOf(f.id);
					if (a !== -1 && b !== -1)
						ids = order.slice(Math.min(a, b), Math.max(a, b) + 1);
				}
				let set = new Set(this.selectedIds);
				for (let id of ids)
				{
					if (checked)
						set.add(id);
					else
						set.delete(id);
				}
				this.selectedIds = this.sortedFindings.map(x => x.id).filter(id => set.has(id));
				this.selectionAnchorId = f.id;
			},
			toggleRow(f, e)
			{
				if (!this.bulkBusy)
					this.setRow(f, !this.isSelected(f), e && e.shiftKey);
			},
			toggleAll(checked)
			{
				this.selectedIds = checked ? this.sortedFindings.map(f => f.id) : [];
				this.selectionAnchorId = null;
			},
			/**
			 * Runs one action per selected finding, one request at a time, through the same API as the finding page (same permissions,
			 * history and activity log).  Findings that failed stay selected so the action can be retried.
			 */
			async runBulk(verb, action)
			{
				if (this.bulkBusy || !this.selectedIds.length)
					return;
				this.bulkBusy = true;
				this.bulkMessage = '';
				this.bulkErrors = [];
				let ids = this.selectedIds.slice();
				let failed = [];
				let changed = 0;
				let unchanged = 0;
				try
				{
					for (let i = 0; i < ids.length; i++)
					{
						this.bulkProgress = verb + ' ' + (i + 1) + ' of ' + ids.length + '…';
						try
						{
							let response = await action(ids[i]);
							if (!response.success)
							{
								failed.push(ids[i]);
								this.bulkErrors.push('CR-' + ids[i] + ': ' + response.error);
							}
							else if (response.changed === false)
								unchanged++;
							else
								changed++;
						}
						catch (ex)
						{
							failed.push(ids[i]);
							this.bulkErrors.push('CR-' + ids[i] + ': ' + ex.message);
						}
					}
				}
				finally
				{
					this.bulkBusy = false;
					this.bulkProgress = '';
					this.slideCounter++;
				}
				this.bulkMessage = verb + ': ' + changed + ' done' + (unchanged ? ', ' + unchanged + ' unchanged' : '') + (failed.length ? ', ' + failed.length + ' failed (still selected)' : '') + '.';
				this.selectedIds = failed;
				this.loadSummary();
				this.load();
				return failed.length === 0;
			},
			openBulkReject()
			{
				this.bulkRejectReason = null;
				this.bulkRejectNote = '';
				this.bulkRejectOpen = true;
				this.$nextTick(() =>
				{
					let b = this.$refs.firstReason;
					if (Array.isArray(b))
						b = b[0];
					if (b)
						b.focus();
				});
			},
			closeBulkReject()
			{
				if (!this.bulkBusy)
					this.bulkRejectOpen = false;
			},
			async confirmBulkReject()
			{
				let reason = this.bulkRejectReason;
				let note = this.bulkRejectNote;
				this.bulkRejectOpen = false;
				await this.runBulk('Rejected', id => SetStatus(id, 'reject', reason, note));
			},
			// The bulk actions are slide buttons, which stand in for confirmation dialogs.
			async bulkMarkFixed()
			{
				await this.runBulk('Marked already fixed', id => SetStatus(id, 'markFixed', null, null));
			},
			async bulkReopen()
			{
				await this.runBulk('Reopened', id => SetStatus(id, 'reopen', null, null));
			},
			async bulkReEvaluate()
			{
				await this.runBulk('Re-evaluation queued', id => ReEvaluate(id));
			},
			/**
			 * Replaces the filters with those in the route query (see routeFilterKeys), keeping the sort order, and removes the
			 * query from the address so that coming back to this page later keeps whatever the user changed since.  Returns
			 * true when the query had filters.
			 */
			applyRouteFilters()
			{
				if (this.$route.name !== 'codeReviewQueue')
					return false;
				let q = this.$route.query || {};
				if (!routeFilterKeys.some(k => q[k] !== undefined))
					return false;
				let f = defaultFilter();
				f.sort = this.filter.sort;
				f.repos = stringList(q.repo);
				if (q.status !== undefined)
					f.statuses = stringList(q.status).filter(s => findingStatuses.indexOf(s) !== -1);
				let minSeverity = stringList(q.minSeverity)[0];
				if (minSeverity && severities.indexOf(minSeverity) !== -1)
					f.minSeverity = minSeverity;
				f.verdicts = stringList(q.verdict).filter(v => verdicts.indexOf(v) !== -1);
				f.categories = stringList(q.category).filter(c => categories.indexOf(c) !== -1);
				f.pushers = stringList(q.pusher);
				f.text = stringList(q.text)[0] || '';
				this.filter = f;
				this.selectedIds = [];
				this.bulkMessage = '';
				this.bulkErrors = [];
				this.$router.replace({ name: 'codeReviewQueue' });
				return true;
			},
			/** The summary's security badge: open security findings, any severity, repository or pusher. */
			showOpenSecurity()
			{
				let f = defaultFilter();
				f.sort = this.filter.sort;
				f.statuses = ['Open'];
				f.categories = ['Security'];
				this.filter = f;
				this.load();
			},
			verdictLabel(v)
			{
				return v === 'None' ? 'Not triaged' : label(v);
			},
			/** Click: sort by this column alone (again: reverse it).  Shift+click: add it as a further sort column (again: reverse it). */
			sortBy(key, e)
			{
				let column = columns.find(c => c.key === key);
				let sort = this.filter.sort.map(s => ({ key: s.key, dir: s.dir }));
				let existing = sort.find(s => s.key === key);
				if (e && e.shiftKey)
				{
					if (existing)
						existing.dir = existing.dir === 'asc' ? 'desc' : 'asc';
					else
						sort.push({ key: key, dir: column.firstDir });
				}
				else if (existing && sort.length === 1)
					existing.dir = existing.dir === 'asc' ? 'desc' : 'asc';
				else
					sort = [{ key: key, dir: existing ? existing.dir : column.firstDir }];
				this.filter.sort = sort;
				saveStored(storageKey, this.filter);
			},
			sortMark(key)
			{
				let i = this.filter.sort.findIndex(s => s.key === key);
				if (i === -1)
					return '';
				let arrow = this.filter.sort[i].dir === 'asc' ? ' ▲' : ' ▼';
				return this.filter.sort.length > 1 ? arrow + (i + 1) : arrow;
			},
			ariaSort(key)
			{
				let first = this.filter.sort[0];
				if (!first || first.key !== key)
					return null;
				return first.dir === 'asc' ? 'ascending' : 'descending';
			},
			findingRoute(f)
			{
				return { name: 'codeReviewFinding', params: { id: f.id } };
			},
			open(f, e)
			{
				// The ID cell holds a real link (keyboard, middle-click); a click anywhere else on the row opens the finding too.
				if (e && e.target && e.target.closest && e.target.closest('a'))
					return;
				this.$router.push(this.findingRoute(f));
			},
			async loadSummary()
			{
				this.summaryError = null;
				try
				{
					let data = await GetSummary();
					if (data.success)
						this.summary = data;
					else
						this.summaryError = data.error;
				}
				catch (ex)
				{
					this.summaryError = ex.message;
				}
			},
			/** Loads the findings.  quiet: keep the rows and the layout as they are (text-only "refreshing" note, no spinner). */
			async load(quiet)
			{
				let token = ++this.loadToken;
				if (quiet === true)
					this.refreshing = true;
				else
					this.loading = true;
				this.error = null;
				saveStored(storageKey, this.filter);
				try
				{
					let data = await ListFindings({
						repos: this.filter.repos,
						statuses: this.filter.statuses,
						minSeverity: this.filter.minSeverity || null,
						verdicts: this.filter.verdicts,
						categories: this.filter.categories,
						pushers: this.filter.pushers,
						text: this.filter.text || null
					});
					if (token !== this.loadToken)
						return;
					if (data.success)
					{
						this.findings = data.findings;
						// Never act on rows that are no longer shown (a filter changed, or the finding left the list).
						let shown = new Set(data.findings.map(f => f.id));
						this.selectedIds = this.selectedIds.filter(id => shown.has(id));
						this.truncated = data.truncated;
						this.limit = data.limit;
					}
					else
					{
						this.findings = [];
						this.error = data.error;
					}
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
					{
						this.loading = false;
						this.refreshing = false;
					}
				}
			},
			filterChanged()
			{
				this.load();
			},
			textChanged()
			{
				clearTimeout(this.textTimer);
				this.textTimer = setTimeout(() => this.load(), 350);
			},
			resetFilters()
			{
				this.filter = defaultFilter();
				this.load();
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.suggestionBadge
	{
		background-color: #E2ECFA;
		color: #123A70;
		border-color: #123A70;
		margin-right: 2px;
	}

	.summary .row
	{
		margin: 2px 0;
	}

	.summaryLabel
	{
		font-weight: bold;
		min-width: 4.5em;
	}

	.summary a.badge
	{
		text-decoration: none;
	}

	.badgeButton
	{
		font-family: inherit;
		cursor: pointer;
	}

		.badgeButton:hover
		{
			text-decoration: underline;
		}

	.rowBadge
	{
		margin-right: 4px;
	}

	tr.securityRow > td:first-child
	{
		box-shadow: inset 4px 0 0 #2D1457;
	}

	.filters
	{
		display: flex;
		flex-wrap: wrap;
		gap: 8px 16px;
		align-items: flex-end;
	}

	.filterItem
	{
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

		.filterItem > label
		{
			font-weight: bold;
			font-size: 12px;
		}

		.filterItem.buttons
		{
			flex-direction: row;
			gap: 6px;
		}

	.statusSet
	{
		border: 1px solid #CCCCCC;
		padding: 2px 8px 4px 8px;
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: 2px 10px;
	}

		.statusSet legend
		{
			font-weight: bold;
			font-size: 12px;
			padding: 0 4px;
		}

	.statusOption
	{
		white-space: nowrap;
	}

	.bulkBar
	{
		position: sticky;
		top: 0;
		z-index: 2;
		background-color: #FFF8E1;
		border: 1px solid #D9C27A;
		padding: 4px 8px;
		margin: 4px 0;
	}

	.selectCell
	{
		width: 1px;
		text-align: center;
		cursor: default;
	}

	tr.selectedRow > td
	{
		background-color: #E8F0FB;
	}

	.legendHint
	{
		font-weight: normal;
		color: #666666;
	}

	.sortButton
	{
		background: none;
		border: none;
		padding: 0;
		font: inherit;
		font-weight: bold;
		color: inherit;
		cursor: pointer;
		white-space: nowrap;
		text-align: left;
	}

		.sortButton:hover
		{
			text-decoration: underline;
		}

	.sortMark
	{
		font-size: 11px;
	}

	.tableCard
	{
		overflow-x: auto;
	}
</style>
